/**
 * Profer, the shop's management program, in words (contract v10.46, ADR 0041 of the back).
 *
 * The back says what went wrong talking to Profer with a stable code, `CLI-01`…`CLI-09`
 * (`cubas.domain.clients`, catalogue in its `docs/architecture.md`, «Códigos de error»).
 * Here is what each one means for the person looking at Clientes or at the picker of a
 * quote. The code is shown too, small: it is what they read out to support.
 *
 * What only the platform can fix (the password of Profer, an answer it cannot read) says
 * «quien mantiene la aplicación», not «quien administra»: the line of Profer is seen by
 * whoever administers the shop, and asking them to warn themselves helps nobody.
 */

const TEXTS: Record<string, string> = {
  'CLI-01':
    'Profer no acepta el usuario o la contraseña de la plataforma. Avisa a quien mantiene la aplicación.',
  'CLI-02': 'Profer tardó demasiado en responder.',
  'CLI-03': 'Profer no estaba disponible en ese momento.',
  'CLI-04': 'Profer tenía la lista de clientes ocupada.',
  'CLI-05':
    'Profer respondió algo que la plataforma no sabe leer. No se ha cambiado ningún cliente. Avisa a quien mantiene la aplicación.',
  'CLI-06': 'Se cortó a medias y la sustituyó la siguiente.',
  'CLI-07': 'No se pudo pedir la actualización.',
  'CLI-08':
    'La lista llegó, pero no se pudo guardar. No se ha cambiado ningún cliente. Avisa a quien mantiene la aplicación.',
};

/** «CLI-02» → «Profer tardó demasiado en responder.» */
export function proferFailureText(code: string | null | undefined): string {
  return (code && TEXTS[code]) || 'Algo ha fallado al hablar con Profer.';
}

/** « (CLI-04)» to put after the sentence, or nothing when the back sent no code. */
export function proferCodeSuffix(code: string | null | undefined): string {
  return code ? ` (${code})` : '';
}

/** Whether waiting fixes it: Profer was slow, down or busy, or the queue did not answer. */
export function proferFailurePasses(code: string | null | undefined): boolean {
  return ['CLI-02', 'CLI-03', 'CLI-04', 'CLI-07'].includes(code ?? '');
}

/**
 * Why the back changed a value, from `ClientSyncCorrection.reason` (the words of
 * `cubas.domain.clients`, without accents), as the report says it. An unknown reason says
 * nothing: the value before and after are on the line already.
 */
export function correctionReasonText(reason: string | null | undefined): string | null {
  const text = (reason ?? '').trim();
  if (!text) return null;
  if (text.startsWith('le faltaban digitos')) return 'le faltaban cifras: rellenado con ceros';
  const known: Record<string, string> = {
    'sin razon social, se usa el nombre comercial': 'sin razón social: se usa el nombre comercial',
    'no es un correo': 'no es una dirección de correo',
    limpiado: 'tenía algo más que el NIF',
    'tenia letras': 'tenía letras',
    'tenia mas de cinco digitos': 'tenía más de cinco cifras',
    'no tiene ningun digito': 'no tiene ninguna cifra',
    'demasiado largo, se ha cortado': 'demasiado largo: se ha cortado',
    'el codigo no es utilizable': 'el código no se puede usar',
  };
  return known[text] ?? null;
}

/** The client fields the report names, as the client card names them. */
export const CLIENT_FIELD_LABELS: Record<string, string> = {
  code: 'Código',
  name: 'Razón social',
  trade_name: 'Nombre comercial',
  nif: 'NIF',
  email: 'Correo',
  phone: 'Teléfono',
  street: 'Dirección',
  postal_code: 'Código postal',
  city: 'Localidad',
  province: 'Provincia',
  representative_code: 'Representante',
  payment_terms: 'Forma de pago',
  re_applies: 'Recargo de equivalencia',
  usual_discount_pct: 'Descuento habitual',
  is_active: 'Activo',
};

export function clientFieldLabel(field: string): string {
  return CLIENT_FIELD_LABELS[field] ?? field;
}
